import React, { useEffect, useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";

const BASE_URL = "https://admin.chatindiacom.cloud/api";

const emptyForm = { title: "", followers_required: "", icon: null };

function FollowerLevelList() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const getToken = () => localStorage.getItem("authToken");

  const api = axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  // This API reports failures with HTTP 200 + response_code "0", so check the body.
  const isOk = (res) => String(res?.data?.response_code) === "1";

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const res = await api.get("/follower-levels/list");
      setLevels(res.data?.levels || []);
    } catch (err) {
      toast.error("Failed to load follower levels");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingLevel(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEditModal = (lvl) => {
    setEditingLevel(lvl);
    setForm({
      title: lvl.title || "",
      followers_required: lvl.followers_required ?? "",
      icon: null,
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (saving) return;
    if (!form.title.trim() || form.followers_required === "") {
      toast.error("Please fill title and followers required");
      return;
    }
    if (Number(form.followers_required) < 0) {
      toast.error("Followers required cannot be negative");
      return;
    }

    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append("followers_required", form.followers_required);
    if (form.icon) fd.append("icon", form.icon);

    setSaving(true);
    try {
      let res;
      if (editingLevel) {
        fd.append("id", editingLevel.id);
        res = await api.post("/follower-levels/edit", fd);
      } else {
        res = await api.post("/follower-levels/create", fd);
      }

      if (isOk(res)) {
        toast.success(
          res.data.message ||
            (editingLevel ? "Follower level updated" : "Follower level added")
        );
        handleClose();
        fetchLevels();
      } else {
        toast.error(res?.data?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this follower level?")) return;
    try {
      const res = await api.post("/follower-levels/delete", { id });
      if (isOk(res)) {
        toast.success(res.data.message || "Follower level deleted");
        fetchLevels();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 dark:bg-primary md:px-8 xl:ml-72">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold font-poppins text-[#000000] dark:text-darkText">
          Follower Levels
        </h2>
        <button
          onClick={openAddModal}
          style={{ backgroundColor: "#6c5ce7" }}
          className="px-4 py-2 text-sm font-semibold text-white rounded-lg font-poppins shadow-sm hover:opacity-90"
        >
          + Add Follower Level
        </button>
      </div>

      <div className="overflow-x-auto bg-white border border-[#F2F2F2] rounded-xl dark:bg-primary dark:border-[#1F1F1F]">
        <table className="w-full text-sm text-left font-poppins">
          <thead>
            <tr className="text-[#000000] dark:text-darkText border-b border-[#F2F2F2] dark:border-[#1F1F1F]">
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Icon</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Followers Required</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                  Loading...
                </td>
              </tr>
            ) : levels.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                  No follower levels found. Click "Add Follower Level" to create one.
                </td>
              </tr>
            ) : (
              levels.map((lvl, index) => (
                <tr
                  key={lvl.id}
                  className="border-b border-[#F2F2F2] dark:border-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                >
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3">
                    {lvl.icon ? (
                      <img
                        src={lvl.icon}
                        alt={lvl.title}
                        className="object-contain w-9 h-9 rounded-md"
                      />
                    ) : (
                      <span className="text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{lvl.title}</td>
                  <td className="px-4 py-3">{lvl.followers_required}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openEditModal(lvl)}
                      className="px-3 py-1 mr-2 text-xs bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText rounded-md"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(lvl.id)}
                      className="px-3 py-1 text-xs text-white bg-red-500 rounded-md"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth className="custom-dialog">
        <DialogContent className="flex flex-col gap-4 dark:bg-primary">
          <h2 className="text-xl text-center font-poppins text-[#000000] dark:text-darkText">
            {editingLevel ? "Edit Follower Level" : "Add Follower Level"}
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Title
            </label>
            <input
              type="text"
              maxLength={100}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. Bronze"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Followers Required (must be unique)
            </label>
            <input
              type="number"
              min={0}
              value={form.followers_required}
              onChange={(e) => setForm({ ...form, followers_required: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Icon (png / jpg / jpeg / webp / gif / svg)
            </label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
              onChange={(e) => setForm({ ...form, icon: e.target.files[0] || null })}
              className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText"
            />
            {editingLevel?.icon && !form.icon && (
              <div className="flex items-center gap-2 pt-1">
                <img
                  src={editingLevel.icon}
                  alt="current icon"
                  className="object-contain w-9 h-9 rounded-md"
                />
                <p className="text-xs text-[#5B5B5B] dark:text-[#AAAAAA]">
                  Current icon (upload a new one to replace)
                </p>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogContent className="flex justify-center gap-4 pb-4 dark:bg-primary">
          <button
            className="w-[150px] py-2 border border-header text-[#3A3333] dark:text-darkText rounded-lg"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ backgroundColor: "#0D9947" }}
            className="w-[150px] py-2 text-white rounded-lg"
          >
            {saving ? (
              <div className="flex justify-center py-1">
                <img src={Loader} alt="loader" height={10} width={10} />
              </div>
            ) : (
              "Save"
            )}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FollowerLevelList;
